import styled from 'styled-components';
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector, shallowEqual } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCustomDataRedirect } from "../common/useCustomDataRedirect";
import AnalyticsBreadcrumb from "ProtoType/NewAnalytics/AnalyticsBreadcrumb";
import Filters from "../Filters";
import { filter_data } from "reports/Tag/calculations";
import { validateAndSanitizeOutcomes } from "../utils/outcomesDataIntegrity";
import { QUADRANT_KEYS, sanitizeQuadrantConfig, computeQuadrantCounts } from "./quadrantUtils";
import InfoHover from './InfoHover';
import {
    buildQuestionIdToPath,
    buildOutcomeQuestionSetByOutcomeName,
    computeQuadrantInclusionForRespondents,
    riskGradeForBottom2,
    scoreAnchorOptionForDimPercent,
    scoreRespondentForDimPercent,
} from "./lviShared";
import {
    extractEmployeeAuthDetails,
    parseAnalyticsPermissionsFromDetails,
} from "utilities/analyticsPermissions";

const CustomAnalytics = ({
    core_data,
    outcomeQ: outcomeQProp,
    data,
    rule,
}) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    
    // Redirect protection for custom data from NewAnalytics
    useCustomDataRedirect();

    // Get employee and permission data for access control
    const { employee, permissionRecords, employeeId } = useSelector(
        (state) => ({
            employee: state.employees?.userEmp,
            permissionRecords: state.employee_permission?.employee_permission,
            employeeId: state.auth?.employee_id,
        }),
        shallowEqual
    );

    const selectedFilters = useSelector(
        (state) => state.audit?.new_filters,
        shallowEqual
    );
    const { raw_data } = useSelector(
        (state) => ({ raw_data: state.audit?.raw_data }),
        shallowEqual
    );
    const hasCustomData = raw_data && Array.isArray(raw_data) && raw_data.length > 0 && raw_data[0]?.datasetMetadata;

    // CRITICAL: Permission check - redirect users without custom_analytics permission
    // This check MUST happen before any rendering to prevent unauthorized access
    // Users CANNOT access this page under ANY circumstances if they do not have the permission
    useEffect(() => {
        const isAdmin = Number(employee?.account_type) === 5;
        
        // Admins have access to everything - no redirect needed
        if (isAdmin) {
            return;
        }

        // Check base analytics access - required for any analytics feature
        if (employee?.access_data_analytics !== 1) {
            console.warn("User does not have base analytics access, redirecting from custom_analytics");
            navigate('/leader/audit/new-analytics', { replace: true });
            return;
        }

        // Check for custom_analytics permission in permission records
        const authDetails = extractEmployeeAuthDetails(permissionRecords, employeeId);
        const parsedAnalytics = parseAnalyticsPermissionsFromDetails(authDetails);

        // SECURITY: Require explicit custom_analytics permission
        // If granular permissions exist, check for custom_analytics
        if (parsedAnalytics) {
            // Granular permissions exist - require explicit custom_analytics permission
            if (parsedAnalytics.custom_analytics !== true) {
                console.warn("User does not have custom_analytics permission, redirecting");
                navigate('/leader/audit/new-analytics', { replace: true });
                return;
            }
        } else {
            // No granular permissions found - for security, require explicit permission
            // This ensures users cannot access custom_analytics without explicit grant
            console.warn("No granular analytics permissions found, custom_analytics requires explicit permission, redirecting");
            navigate('/leader/audit/new-analytics', { replace: true });
            return;
        }
    }, [employee, permissionRecords, employeeId, navigate]);

    const [correctedData, setCorrectedData] = useState(null);
    const [selectedAnchorCategoryId, setSelectedAnchorCategoryId] = useState(null);
    const [sortKey, setSortKey] = useState('risk'); // locked to Risk (derived from Bottom 2 %)
    const [sortDir, setSortDir] = useState('desc'); // locked to high-to-low risk
    const [isComputing, setIsComputing] = useState(false);
    const [computedRows, setComputedRows] = useState([]); // rows with quadrant inclusion stats

    const [quadrantConfig, setQuadrantConfig] = useState(() =>
        sanitizeQuadrantConfig({
            xCutoff: 60,
            yCutoff: 60,
            subRect: { xmin: 0, xmax: 40, ymin: 0, ymax: 40 }, // default SUB inside BL
        })
    );
    const [showSettings, setShowSettings] = useState(false);
    const [breakdownMode, setBreakdownMode] = useState('persona'); // 'persona' | 'risk'

    const [riskThresholds, setRiskThresholds] = useState(() => ({
        veryHigh: 35,
        high: 30,
        moderate: 20,
        stable: 10,
        low: 5,
    }));

    // Prepare filtered data (respecting existing Filters component behavior)
    useEffect(() => {
        if (!data) return;
        let filteredData = [];

        if (Array.isArray(data) && data.length === 0 && selectedFilters.length === 0) {
            return;
        }

        if (Array.isArray(data) && data.length === 0) {
            setCorrectedData([]);
            return;
        }

        if (data?.[0]?.[0]) {
            filteredData = filter_data(data[0], selectedFilters);
            setCorrectedData(filteredData);
            return;
        }

        filteredData = filter_data(data, selectedFilters);
        setCorrectedData(filteredData);
    }, [data, selectedFilters]);

    // Prefer questions from custom dataset if present, else fall back to core_data.questions
    const questionsSource = useMemo(() => {
        if (hasCustomData && raw_data?.[0]?.question) return raw_data[0].question;
        return core_data?.questions;
    }, [hasCustomData, raw_data, core_data]);

    // Read analytics schema from preferred questions source
    const analyticsSchema = questionsSource?.analytics;

    // Build mapping from global question id -> {dimensionIndex, factorIndex, questionIndex, reverse}
    const questionIdToPath = useMemo(() => {
        return buildQuestionIdToPath(questionsSource?.dimensions || []);
    }, [questionsSource?.dimensions]);

    // Sanitize outcomeQ to avoid duplicate IDs / malformed sections
    const outcomeQ = useMemo(() => {
        if (!outcomeQProp) return null;
        try {
            const { sanitized } = validateAndSanitizeOutcomes(outcomeQProp);
            return sanitized;
        } catch (e) {
            return outcomeQProp;
        }
    }, [outcomeQProp]);

    const analysisDef = analyticsSchema?.analyses;

    const minResponses = useMemo(() => Number(core_data?.Whitelabel?.rule ?? rule ?? 2), [core_data?.Whitelabel?.rule, rule]);

    // Pick the two dimensions for this analysis (prefer analysisDef.dimensions, fallback to first two schema dimensions)
    const [dim1Def, dim2Def] = useMemo(() => {
        const schemaDims = Array.isArray(analyticsSchema?.dimensions) ? analyticsSchema.dimensions : [];
        const analysisDims = Array.isArray(analysisDef?.dimensions) ? analysisDef.dimensions : [];

        const resolveDim = (analysisDim) => {
            if (!analysisDim) return null;
            // analysisDim.key refers to schema dimension key
            const found = schemaDims.find(d => String(d.key) === String(analysisDim.key));
            return found || null;
        };

        const d1 = resolveDim(analysisDims[0]) || schemaDims[0] || null;
        const d2 = resolveDim(analysisDims[1]) || schemaDims[1] || null;
        return [d1, d2];
    }, [analyticsSchema?.dimensions, analysisDef?.dimensions]);

    const dim1Name = dim1Def?.name?.en || dim1Def?.key || 'Dimension 1';
    const dim2Name = dim2Def?.name?.en || dim2Def?.key || 'Dimension 2';
    const isDim1Engagement = useMemo(() => {
        const key = String(dim1Def?.key ?? dim1Name ?? '').toLowerCase();
        const name = String(dim1Name ?? '').toLowerCase();
        return key.includes('engagement') || name.includes('engagement');
    }, [dim1Def?.key, dim1Name]);
    const title = `LVI Analysis`;
    const QUADRANT_COLORS = useMemo(() => ({
        [QUADRANT_KEYS.TL]: '#2D70E2',
        [QUADRANT_KEYS.TR]: '#10B981',
        // Skeptic (BL) should be yellow; Situational (BR) should be purple.
        [QUADRANT_KEYS.BL]: '#F59E0B',
        [QUADRANT_KEYS.BR]: '#7F56D9',
        // Disconnected (SUB): keep distinct but not purple.
        [QUADRANT_KEYS.SUB]: '#EF4444',
        noScore: '#C7CFDB',
    }), []);

    const QUADRANT_LABELS = useMemo(() => ({
        [QUADRANT_KEYS.TR]: 'Champion',
        [QUADRANT_KEYS.TL]: 'Supportive',
        [QUADRANT_KEYS.BR]: 'Situational',
        [QUADRANT_KEYS.BL]: 'Skeptic',
        [QUADRANT_KEYS.SUB]: 'Disconnected',
    }), []);

    // Build anchor category options
    const anchorCategories = useMemo(() => {
        const cats = core_data?.categories?.[0]?.categories || [];
        if (!cats.length) return [];
        const nonPrimary = cats.filter(c => (c?.priority || '').toLowerCase() !== 'primary');
        const primary = cats.find(c => (c?.priority || '').toLowerCase() === 'primary');
        const list = [];
        if (primary) {
            list.push({ id: primary.id, name: 'Overview' });
        }
        nonPrimary.forEach(c => list.push({ id: c.id, name: c.name }));
        return list;
    }, [core_data?.categories]);

    useEffect(() => {
        if (!selectedAnchorCategoryId && anchorCategories?.[0]) {
            setSelectedAnchorCategoryId(anchorCategories[0].id);
        }
    }, [anchorCategories, selectedAnchorCategoryId]);

    const anchorOptionsForSelectedCategory = useMemo(() => {
        if (!selectedAnchorCategoryId) return [];
        const cat = core_data?.categories?.[0]?.categories?.find(c => String(c.id) === String(selectedAnchorCategoryId));
        return cat?.options || [];
    }, [core_data?.categories, selectedAnchorCategoryId]);

    // Determine which anchor categories have at least one option with sufficient data
    const validAnchorCategories = useMemo(() => {
        if (!correctedData || !anchorCategories?.length) return anchorCategories || [];
        return anchorCategories.filter(cat => {
            const catDef = core_data?.categories?.[0]?.categories?.find(c => String(c.id) === String(cat.id));
            const options = catDef?.options || [];
            // category is valid if any option has respondents > minResponses
            return options.some(opt => {
                const respondents = correctedData.filter(r => {
                    const match = r?.categories?.find(c => String(c.id) === String(cat.id));
                    return match && String(match.response) === String(opt.id);
                });
                return respondents.length > minResponses;
            });
        });
    }, [anchorCategories, correctedData, core_data?.categories, minResponses]);

    // Ensure selected anchor is valid; if not, set to first valid
    useEffect(() => {
        if (!validAnchorCategories?.length) return;
        const isSelectedValid = validAnchorCategories.some(a => a.id === selectedAnchorCategoryId);
        if (!isSelectedValid) {
            setSelectedAnchorCategoryId(validAnchorCategories[0].id);
        }
    }, [validAnchorCategories, selectedAnchorCategoryId]);

    const outcomeQuestionSetByOutcomeName = useMemo(() => buildOutcomeQuestionSetByOutcomeName(outcomeQ), [outcomeQ]);
    const lviDeps = useMemo(() => ({ questionIdToPath, outcomeQuestionSetByOutcomeName }), [questionIdToPath, outcomeQuestionSetByOutcomeName]);

    // Compute table rows asynchronously so we can show a real loading state when switching category/filters.
    useEffect(() => {
        let cancelled = false;

        const run = async () => {
            if (!correctedData || !selectedAnchorCategoryId) {
                setComputedRows([]);
                return;
            }

            setIsComputing(true);

            // Yield to the browser so the loading UI can paint before heavy work starts.
            await new Promise((resolve) => setTimeout(resolve, 0));
            if (cancelled) return;

            const options = anchorOptionsForSelectedCategory || [];

            // Pre-group respondents by selected anchor option (avoids O(options * respondents)).
            const byOptionId = new Map();
            correctedData.forEach(r => {
                const match = r?.categories?.find(c => String(c.id) === String(selectedAnchorCategoryId));
                const optId = match?.response;
                if (optId == null || optId === '') return;
                const key = String(optId);
                const arr = byOptionId.get(key) || [];
                arr.push(r);
                byOptionId.set(key, arr);
            });

            const baseRows = options
                .map(opt => {
                    const respondents = byOptionId.get(String(opt.id)) || [];
                    if (respondents.length < 5) return null; // privacy/threshold: hide small groups

                    const dim1ScorePct = scoreAnchorOptionForDimPercent(respondents, dim1Def, minResponses, lviDeps);
                    const dim2ScorePct = scoreAnchorOptionForDimPercent(respondents, dim2Def, minResponses, lviDeps);
                    const quadrant = computeQuadrantInclusionForRespondents(respondents, dim1Def, dim2Def, quadrantConfig, lviDeps);

                    // Bottom 2 is always Skeptic (BL) + Disconnected (SUB)
                    const bottom2Count = Number(quadrant?.counts?.[QUADRANT_KEYS.BL] || 0) + Number(quadrant?.counts?.[QUADRANT_KEYS.SUB] || 0);
                    const bottom2Pct = Number(quadrant?.percents?.[QUADRANT_KEYS.BL] || 0) + Number(quadrant?.percents?.[QUADRANT_KEYS.SUB] || 0);
                    const risk = riskGradeForBottom2(bottom2Pct, riskThresholds);

                    return {
                        id: opt.id,
                        name: opt.name,
                        respondentsCount: respondents.length,
                        dim1ScorePct,
                        dim2ScorePct,
                        quadrantEligibleCount: quadrant.eligibleCount,
                        qTLPct: quadrant.percents[QUADRANT_KEYS.TL],
                        qTRPct: quadrant.percents[QUADRANT_KEYS.TR],
                        qBLPct: quadrant.percents[QUADRANT_KEYS.BL],
                        qBRPct: quadrant.percents[QUADRANT_KEYS.BR],
                        qSUBPct: quadrant.percents[QUADRANT_KEYS.SUB],
                        qCounts: quadrant.counts,
                        qBottom2Pct: bottom2Pct,
                        qBottom2Count: bottom2Count,
                        riskLabel: risk.label,
                        riskKey: risk.key,
                        riskColor: risk.color,
                    };
                })
                .filter(Boolean);

            if (cancelled) return;
            setComputedRows(baseRows);
            setIsComputing(false);
        };

        run();
        return () => { cancelled = true; };
    }, [
        correctedData,
        selectedAnchorCategoryId,
        anchorOptionsForSelectedCategory,
        dim1Def,
        dim2Def,
        minResponses,
        quadrantConfig,
        riskThresholds,
    ]);

    const sortedRows = useMemo(() => {
        const dir = String(sortDir) === 'asc' ? 1 : -1;
        const asNumberOrNull = (v) => (v == null || isNaN(Number(v)) ? null : Number(v));
        const asString = (v) => String(v ?? '').toLowerCase();

        const getVal = (row) => {
            switch (sortKey) {
                case 'name': return asString(row?.name);
                case 'x': return asNumberOrNull(row?.dim1ScorePct);
                case 'tr': return asNumberOrNull(row?.qTRPct);
                case 'tl': return asNumberOrNull(row?.qTLPct);
                case 'br': return asNumberOrNull(row?.qBRPct);
                case 'bl': return asNumberOrNull(row?.qBLPct);
                case 'sub': return asNumberOrNull(row?.qSUBPct);
                case 'risk':
                default:
                    // Risk is derived from Bottom 2% (BL + SUB); sort by the underlying percent.
                    return asNumberOrNull(row?.qBottom2Pct);
            }
        };

        const isTextSort = sortKey === 'name';

        return [...computedRows].sort((a, b) => {
            const av = getVal(a);
            const bv = getVal(b);

            // Nulls last
            if (av == null && bv == null) return asString(a?.name).localeCompare(asString(b?.name));
            if (av == null) return 1;
            if (bv == null) return -1;

            if (isTextSort) {
                if (av !== bv) return av.localeCompare(bv) * dir;
                return asString(a?.name).localeCompare(asString(b?.name));
            }

            if (av !== bv) return (av - bv) * dir;
            return asString(a?.name).localeCompare(asString(b?.name));
        });
    }, [computedRows, sortKey, sortDir]);

    const quadrantSummary = useMemo(() => {
        const emptyCounts = {
            [QUADRANT_KEYS.TL]: 0,
            [QUADRANT_KEYS.TR]: 0,
            [QUADRANT_KEYS.BL]: 0,
            [QUADRANT_KEYS.BR]: 0,
            [QUADRANT_KEYS.SUB]: 0,
        };

        if (!correctedData || !selectedAnchorCategoryId || !dim1Def || !dim2Def) {
            return {
                counts: emptyCounts,
                totalRespondents: 0,
                totalScored: 0,
                noScoreCount: 0,
                percents: { ...emptyCounts, noScore: 0 },
            };
        }

        const inCategory = (correctedData || []).filter(r => {
            const match = r?.categories?.find(c => String(c.id) === String(selectedAnchorCategoryId));
            const optId = match?.response;
            return !(optId == null || optId === '');
        });

        const totalRespondents = inCategory.length;
        const points = inCategory
            .map(r => ({
                x: scoreRespondentForDimPercent(r, dim1Def, lviDeps),
                y: scoreRespondentForDimPercent(r, dim2Def, lviDeps),
            }))
            .filter(p => Number.isFinite(Number(p.x)) && Number.isFinite(Number(p.y)));

        const { counts, total: totalScored } = computeQuadrantCounts(points, quadrantConfig);
        const noScoreCount = Math.max(0, totalRespondents - totalScored);

        const denom = totalRespondents || 0;
        const pct = (n) => (denom > 0 ? (Number(n || 0) / denom) * 100 : 0);

        return {
            counts,
            totalRespondents,
            totalScored,
            noScoreCount,
            percents: {
                [QUADRANT_KEYS.TL]: pct(counts?.[QUADRANT_KEYS.TL]),
                [QUADRANT_KEYS.TR]: pct(counts?.[QUADRANT_KEYS.TR]),
                [QUADRANT_KEYS.BL]: pct(counts?.[QUADRANT_KEYS.BL]),
                [QUADRANT_KEYS.BR]: pct(counts?.[QUADRANT_KEYS.BR]),
                [QUADRANT_KEYS.SUB]: pct(counts?.[QUADRANT_KEYS.SUB]),
                noScore: pct(noScoreCount),
            },
        };
    }, [correctedData, selectedAnchorCategoryId, dim1Def, dim2Def, quadrantConfig]);

    const riskSummary = useMemo(() => {
        const order = ['very_high', 'high', 'moderate', 'stable', 'low'];
        const labels = {
            very_high: 'Very High Risk',
            high: 'High Risk',
            moderate: 'Moderate Risk',
            stable: 'Generally Stable',
            low: 'Low Risk',
        };
        const colors = {
            very_high: '#EF4444',
            high: '#F97316',
            moderate: '#F59E0B',
            stable: '#2D70E2',
            low: '#10B981',
        };

        const counts = order.reduce((acc, k) => ({ ...acc, [k]: 0 }), {});
        const groupCounts = order.reduce((acc, k) => ({ ...acc, [k]: 0 }), {});

        (computedRows || []).forEach((row) => {
            const key = String(row?.riskKey || '');
            if (!order.includes(key)) return;
            const n = Number(row?.respondentsCount || 0);
            counts[key] += n;
            groupCounts[key] += 1;
        });

        const totalRespondents = order.reduce((s, k) => s + Number(counts[k] || 0), 0);
        const pct = (n) => (totalRespondents > 0 ? (Number(n || 0) / totalRespondents) * 100 : 0);

        const percents = order.reduce((acc, k) => ({ ...acc, [k]: pct(counts[k]) }), {});

        return {
            order,
            labels,
            colors,
            counts,
            groupCounts,
            totalRespondents,
            percents,
        };
    }, [computedRows]);

    const csvEscape = (value) => {
        const s = value == null ? '' : String(value);
        // Escape double quotes by doubling them; wrap in quotes if special chars present
        const needsQuotes = /[",\n\r]/.test(s);
        const escaped = s.replace(/"/g, '""');
        return needsQuotes ? `"${escaped}"` : escaped;
    };

    const downloadCsv = () => {
        const categoryName = validAnchorCategories.find(a => a.id === selectedAnchorCategoryId)?.name || 'Category';
        const headers = [
            'Name',
            `${dim1Name} Score (%)`,
            'Champion (%)',
            'Supportive (%)',
            'Situational (%)',
            'Skeptic (%)',
            'Disconnected (%)',
            'Risk Grade',
        ];

        const rows = (sortedRows || []).map(r => ([
            `${r.name}${r.respondentsCount == null ? '' : ` (n=${r.respondentsCount})`}`,
            r.dim1ScorePct == null ? '' : Math.round(r.dim1ScorePct),
            r.qTRPct == null ? '' : Math.round(r.qTRPct),
            r.qTLPct == null ? '' : Math.round(r.qTLPct),
            r.qBRPct == null ? '' : Math.round(r.qBRPct),
            r.qBLPct == null ? '' : Math.round(r.qBLPct),
            r.qSUBPct == null ? '' : Math.round(r.qSUBPct),
            r.riskLabel || '',
        ]));

        const csv = [
            headers.map(csvEscape).join(','),
            ...rows.map(cols => cols.map(csvEscape).join(',')),
        ].join('\r\n');

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);

        const safeName = (s) => String(s || '')
            .replace(/[<>:"/\\|?*\u0000-\u001F]/g, '')
            .replace(/\s+/g, ' ')
            .trim()
            .slice(0, 120);

        const filename = `${safeName(title)} - ${safeName(categoryName)}.csv` || 'table.csv';

        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    const onSort = (key) => {
        const nextKey = key || 'risk';
        setSortKey((prev) => {
            if (prev === nextKey) {
                setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                return prev;
            }
            setSortDir(nextKey === 'name' ? 'asc' : 'desc');
            return nextKey;
        });
    };

    const onHeaderKeyDown = (e, key) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSort(key);
        }
    };

    // CRITICAL: Permission check before rendering - prevent any unauthorized access
    // This synchronous check ensures no content is rendered if user lacks permission
    const isAdmin = Number(employee?.account_type) === 5;
    const hasBaseAnalyticsAccess = employee?.access_data_analytics === 1;
    
    let hasCustomAnalyticsPermission = false;
    if (isAdmin) {
        hasCustomAnalyticsPermission = true; // Admins have access
    } else if (hasBaseAnalyticsAccess) {
        const authDetails = extractEmployeeAuthDetails(permissionRecords, employeeId);
        const parsedAnalytics = parseAnalyticsPermissionsFromDetails(authDetails);
        if (parsedAnalytics) {
            hasCustomAnalyticsPermission = parsedAnalytics.custom_analytics === true;
        }
        // If no granular permissions exist, deny access for security
    }

    // If user doesn't have permission, return null immediately (redirect will happen in useEffect)
    if (!hasCustomAnalyticsPermission) {
        return null;
    }

    if (!core_data) {
        return <div>Loading...</div>;
    }

    if (!analyticsSchema || !analysisDef) {
        return (
            <Container>
                <AnalyticsBreadcrumb currentComponent="custom" />
                <HeaderRow>
                    {/* <BlueTitle>Culture Analytics</BlueTitle> */}
                    <Title>
                        <TitleIconWrapper aria-hidden>
                            <QuadrantIcon />
                        </TitleIconWrapper>
                        Custom Analytics
                    </Title>
                </HeaderRow>
                <EmptyState>Analytics schema not found for this survey.</EmptyState>
            </Container>
        );
    }

    return (
        <Container>
            <AnalyticsBreadcrumb currentComponent="custom" />
            <HeaderRow>
                {/* <BlueTitle>Culture Analytics</BlueTitle> */}
                <Title>
                    <TitleIconWrapper aria-hidden>
                        <QuadrantIcon />
                    </TitleIconWrapper>
                    {title}
                </Title>
            </HeaderRow>

            <OptionsContainer>
                <Controls>
                    <ControlGroup>
                        <Label>Category</Label>
                        <Select value={selectedAnchorCategoryId || ''} onChange={(e) => setSelectedAnchorCategoryId(e.target.value)}>
                            {validAnchorCategories.map(cat => (
                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                            ))}
                        </Select>
                    </ControlGroup>
                </Controls>
                <TopRightActions>
                    <GhostButton
                        type="button"
                        onClick={() => {
                            const dataset = searchParams.get('dataset');
                            const qs = dataset ? `?dataset=${encodeURIComponent(dataset)}` : '';
                            navigate(`/leader/audit/simple/bulk-lvi${qs}`);
                        }}
                    >
                        Bulk LVI (CSV)
                    </GhostButton>
                </TopRightActions>
            </OptionsContainer>

            <FiltersRow>
                <Filters />
            </FiltersRow>

            <Content>
                <Section>
                    <SectionHeader>
                        <SectionTitle>By {validAnchorCategories.find(a => a.id === selectedAnchorCategoryId)?.name || 'Category'}</SectionTitle>
                        <Actions>
                            <CsvButton type="button" onClick={downloadCsv} disabled={!sortedRows.length}>
                                Save CSV
                            </CsvButton>
                        </Actions>
                    </SectionHeader>
                    <SummaryRow>
                        <SummaryLeft>
                            <SummaryTitle>Cohort</SummaryTitle>
                            <SummaryMeta>
                                {computedRows.length} groups • {quadrantSummary.totalRespondents} respondents • {quadrantSummary.totalScored} scored
                                {isComputing ? ' • Calculating…' : ''}
                            </SummaryMeta>
                        </SummaryLeft>
                        <SummaryRight>
                            <ModeToggle role="tablist" aria-label="Breakdown toggle">
                                <ModeButton
                                    type="button"
                                    role="tab"
                                    aria-selected={breakdownMode === 'persona'}
                                    $active={breakdownMode === 'persona'}
                                    onClick={() => setBreakdownMode('persona')}
                                >
                                    Personas
                                </ModeButton>
                                <ModeButton
                                    type="button"
                                    role="tab"
                                    aria-selected={breakdownMode === 'risk'}
                                    $active={breakdownMode === 'risk'}
                                    onClick={() => setBreakdownMode('risk')}
                                >
                                    Risk
                                </ModeButton>
                            </ModeToggle>
                        </SummaryRight>
                    </SummaryRow>
                    <SeverityBarWrap>
                        <SeverityBar>
                            {breakdownMode === 'persona' && (
                                <>
                                    {[QUADRANT_KEYS.TR, QUADRANT_KEYS.TL, QUADRANT_KEYS.BR, QUADRANT_KEYS.BL, QUADRANT_KEYS.SUB].map((k) => {
                                        const pct = quadrantSummary.percents?.[k] || 0;
                                        const count = quadrantSummary.counts?.[k] || 0;
                                        return (
                                            <SeveritySegment
                                                key={k}
                                                style={{
                                                    width: `${pct}%`,
                                                    backgroundColor: QUADRANT_COLORS[k],
                                                }}
                                                title={`${QUADRANT_LABELS[k] || k}: ${count} (${Math.round(pct)}%)`}
                                            />
                                        );
                                    })}
                                    {quadrantSummary.noScoreCount > 0 && (
                                        <SeveritySegment
                                            style={{
                                                width: `${quadrantSummary.percents?.noScore || 0}%`,
                                                backgroundColor: QUADRANT_COLORS.noScore,
                                            }}
                                            title={`No score: ${quadrantSummary.noScoreCount}`}
                                        />
                                    )}
                                </>
                            )}
                            {breakdownMode === 'risk' && (
                                <>
                                    {riskSummary.order.map((k) => {
                                        const pct = riskSummary.percents?.[k] || 0;
                                        const count = riskSummary.counts?.[k] || 0;
                                        const label = riskSummary.labels?.[k] || k;
                                        const groupCount = riskSummary.groupCounts?.[k] || 0;
                                        return (
                                            <SeveritySegment
                                                key={k}
                                                style={{
                                                    width: `${pct}%`,
                                                    backgroundColor: riskSummary.colors?.[k] || '#C7CFDB',
                                                }}
                                                title={`${label}: ${count} respondents (${Math.round(pct)}%) • ${groupCount} groups`}
                                            />
                                        );
                                    })}
                                </>
                            )}
                        </SeverityBar>
                        <SeverityCounts>
                            {breakdownMode === 'persona' && (
                                <>
                                    {[QUADRANT_KEYS.TR, QUADRANT_KEYS.TL, QUADRANT_KEYS.BR, QUADRANT_KEYS.BL, QUADRANT_KEYS.SUB].map((k) => (
                                        <SeverityCount key={k}>
                                            <LegendSwatch style={{ backgroundColor: QUADRANT_COLORS[k] }} />
                                            <span>
                                                {QUADRANT_LABELS[k] || k}: {quadrantSummary.counts?.[k] || 0}
                                                {' '}
                                                ({Math.round(quadrantSummary.percents?.[k] || 0)}%)
                                            </span>
                                        </SeverityCount>
                                    ))}
                                    {quadrantSummary.noScoreCount > 0 && (
                                        <SeverityCount>
                                            <LegendSwatch style={{ backgroundColor: QUADRANT_COLORS.noScore }} />
                                            <span>
                                                No score: {quadrantSummary.noScoreCount}
                                                {' '}
                                                ({Math.round(quadrantSummary.percents?.noScore || 0)}%)
                                            </span>
                                        </SeverityCount>
                                    )}
                                </>
                            )}
                            {breakdownMode === 'risk' && (
                                <>
                                    {riskSummary.order.map((k) => (
                                        <SeverityCount key={k}>
                                            <LegendSwatch style={{ backgroundColor: riskSummary.colors?.[k] || '#C7CFDB' }} />
                                            <span>
                                                {riskSummary.labels?.[k] || k}: {riskSummary.counts?.[k] || 0}
                                                {' '}
                                                ({Math.round(riskSummary.percents?.[k] || 0)}%)
                                            </span>
                                        </SeverityCount>
                                    ))}
                                </>
                            )}
                        </SeverityCounts>
                    </SeverityBarWrap>
                    <TableWrap>
                        {isComputing && (
                            <TableOverlay>
                                <Spinner />
                                <OverlayText>Calculating…</OverlayText>
                            </TableOverlay>
                        )}
                        <Table>
                            <colgroup>
                                <col style={{ width: '30%' }} />
                                <col style={{ width: '12%' }} />
                                <col style={{ width: '9%' }} />
                                <col style={{ width: '9%' }} />
                                <col style={{ width: '9%' }} />
                                <col style={{ width: '9%' }} />
                                <col style={{ width: '9%' }} />
                                <col style={{ width: '13%' }} />
                            </colgroup>
                            <thead>
                                <tr>
                                    <Th
                                        onClick={() => onSort('name')}
                                        onKeyDown={(e) => onHeaderKeyDown(e, 'name')}
                                        role="button"
                                        tabIndex={0}
                                    >
                                        <ThInner>
                                            <HeaderText title="Name">Name</HeaderText>
                                            <SortHint active={sortKey === 'name'}>{sortKey === 'name' ? (sortDir === 'asc' ? '↑' : '↓') : ''}</SortHint>
                                        </ThInner>
                                    </Th>
                                    <ThRightHighlight
                                        onClick={() => onSort('x')}
                                        onKeyDown={(e) => onHeaderKeyDown(e, 'x')}
                                        role="button"
                                        tabIndex={0}
                                    >
                                        <ThInner>
                                            <HeaderText title={`${dim1Name} Score (%)`}>{dim1Name} Score (%)</HeaderText>
                                            {isDim1Engagement && (
                                                <InfoHover
                                                    ariaLabel="Engagement info"
                                                    text="Pride in working at Staples, motivation, and belief in Staples values (average of those 3 questions)."
                                                />
                                            )}
                                            <SortHint active={sortKey === 'x'}>{sortKey === 'x' ? (sortDir === 'asc' ? '↑' : '↓') : ''}</SortHint>
                                        </ThInner>
                                    </ThRightHighlight>
                                    <ThRight
                                        onClick={() => onSort('tr')}
                                        onKeyDown={(e) => onHeaderKeyDown(e, 'tr')}
                                        role="button"
                                        tabIndex={0}
                                    >
                                        <ThInner>
                                            <HeaderText title="Champion (%)">Champion %</HeaderText>
                                            <SortHint active={sortKey === 'tr'}>{sortKey === 'tr' ? (sortDir === 'asc' ? '↑' : '↓') : ''}</SortHint>
                                        </ThInner>
                                    </ThRight>
                                    <ThRight
                                        onClick={() => onSort('tl')}
                                        onKeyDown={(e) => onHeaderKeyDown(e, 'tl')}
                                        role="button"
                                        tabIndex={0}
                                    >
                                        <ThInner>
                                            <HeaderText title="Supportive (%)">Supportive %</HeaderText>
                                            <SortHint active={sortKey === 'tl'}>{sortKey === 'tl' ? (sortDir === 'asc' ? '↑' : '↓') : ''}</SortHint>
                                        </ThInner>
                                    </ThRight>
                                    <ThRight
                                        onClick={() => onSort('br')}
                                        onKeyDown={(e) => onHeaderKeyDown(e, 'br')}
                                        role="button"
                                        tabIndex={0}
                                    >
                                        <ThInner>
                                            <HeaderText title="Situational (%)">Situational %</HeaderText>
                                            <SortHint active={sortKey === 'br'}>{sortKey === 'br' ? (sortDir === 'asc' ? '↑' : '↓') : ''}</SortHint>
                                        </ThInner>
                                    </ThRight>
                                    <ThRight
                                        onClick={() => onSort('bl')}
                                        onKeyDown={(e) => onHeaderKeyDown(e, 'bl')}
                                        role="button"
                                        tabIndex={0}
                                    >
                                        <ThInner>
                                            <HeaderText title="Skeptic (%)">Skeptic %</HeaderText>
                                            <SortHint active={sortKey === 'bl'}>{sortKey === 'bl' ? (sortDir === 'asc' ? '↑' : '↓') : ''}</SortHint>
                                        </ThInner>
                                    </ThRight>
                                    <ThRight
                                        onClick={() => onSort('sub')}
                                        onKeyDown={(e) => onHeaderKeyDown(e, 'sub')}
                                        role="button"
                                        tabIndex={0}
                                    >
                                        <ThInner>
                                            <HeaderText title="Disconnected (%)">Disconnected %</HeaderText>
                                            <SortHint active={sortKey === 'sub'}>{sortKey === 'sub' ? (sortDir === 'asc' ? '↑' : '↓') : ''}</SortHint>
                                        </ThInner>
                                    </ThRight>
                                    <Th
                                        onClick={() => onSort('risk')}
                                        onKeyDown={(e) => onHeaderKeyDown(e, 'risk')}
                                        role="button"
                                        tabIndex={0}
                                    >
                                        <ThInner>
                                            <HeaderText title="Risk">Risk</HeaderText>
                                            <InfoHover
                                                ariaLabel="Risk info"
                                                placement="right"
                                                text="Risk is calculated by grouping the Skeptic and Disconnected personas together as the bottom 2. The higher the bottom 2 percentage, the higher the overall risk for that group."
                                            />
                                            <SortHint active={sortKey === 'risk'}>{sortKey === 'risk' ? (sortDir === 'asc' ? '↑' : '↓') : ''}</SortHint>
                                        </ThInner>
                                    </Th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedRows.map(row => (
                                    <Tr key={row.id}>
                                        <Td>
                                            <NameCell>
                                                <NameLine>
                                                    <NamePrimary title={row.name}>{row.name}</NamePrimary>
                                                    {row.respondentsCount == null ? null : <NameCount>{`(n=${row.respondentsCount})`}</NameCount>}
                                                </NameLine>
                                                <Meta>
                                                    {row.quadrantEligibleCount !== row.respondentsCount ? ` • scored=${row.quadrantEligibleCount}` : ''}
                                                </Meta>
                                            </NameCell>
                                        </Td>
                                        <TdRightHighlight>{row.dim1ScorePct == null ? '--' : `${Math.round(row.dim1ScorePct)}%`}</TdRightHighlight>
                                        <TdRight>{row.qTRPct == null ? '--' : `${Math.round(row.qTRPct)}%`}</TdRight>
                                        <TdRight>{row.qTLPct == null ? '--' : `${Math.round(row.qTLPct)}%`}</TdRight>
                                        <TdRight>{row.qBRPct == null ? '--' : `${Math.round(row.qBRPct)}%`}</TdRight>
                                        <TdRight>{row.qBLPct == null ? '--' : `${Math.round(row.qBLPct)}%`}</TdRight>
                                        <TdRight>{row.qSUBPct == null ? '--' : `${Math.round(row.qSUBPct)}%`}</TdRight>
                                        <Td>
                                            <RiskLabel style={{ color: row.riskColor || '#9CA3AF' }}>
                                                {row.riskLabel || '--'}
                                            </RiskLabel>
                                        </Td>
                                    </Tr>
                                ))}
                                {!sortedRows.length && (
                                    <Tr>
                                        <Td colSpan={8}>
                                            <EmptyInline>No data available for this category.</EmptyInline>
                                        </Td>
                                    </Tr>
                                )}
                            </tbody>
                        </Table>
                    </TableWrap>
                </Section>
            </Content>

            {showSettings && createPortal(
                <ModalOverlay
                    role="presentation"
                    onMouseDown={(e) => {
                        // Click on backdrop closes; click inside modal does not.
                        if (e.target === e.currentTarget) setShowSettings(false);
                    }}
                >
                    <ModalCard role="dialog" aria-modal="true" aria-label="Analytics settings">
                        <ModalHeader>
                            <ModalTitle>Analytics Settings</ModalTitle>
                            <ModalClose type="button" onClick={() => setShowSettings(false)}>
                                Close
                            </ModalClose>
                        </ModalHeader>

                        <ModalBody>
                            <ModalSection>
                                <ModalSectionTitle>Quadrants</ModalSectionTitle>
                                <SettingsGrid>
                                    <ControlGroup>
                                        <Label>{dim1Name} cutoff (X)</Label>
                                        <Input
                                            type="number"
                                            min={0}
                                            max={100}
                                            step={1}
                                            value={quadrantConfig.xCutoff}
                                            onChange={(e) => {
                                                const value = e?.target?.value;
                                                setQuadrantConfig(prev => sanitizeQuadrantConfig({
                                                    ...prev,
                                                    xCutoff: value,
                                                    subRect: prev.subRect,
                                                }));
                                            }}
                                        />
                                    </ControlGroup>
                                    <ControlGroup>
                                        <Label>{dim2Name} cutoff (Y)</Label>
                                        <Input
                                            type="number"
                                            min={0}
                                            max={100}
                                            step={1}
                                            value={quadrantConfig.yCutoff}
                                            onChange={(e) => {
                                                const value = e?.target?.value;
                                                setQuadrantConfig(prev => sanitizeQuadrantConfig({
                                                    ...prev,
                                                    yCutoff: value,
                                                    subRect: prev.subRect,
                                                }));
                                            }}
                                        />
                                    </ControlGroup>
                                </SettingsGrid>
                            </ModalSection>

                            <ModalSection>
                                <ModalSectionTitle>Sub-quadrant (inside BL, exclusive)</ModalSectionTitle>
                                <SubRow>
                                    <MiniField>
                                        <MiniLabel>Xmin</MiniLabel>
                                        <MiniInput
                                            type="number"
                                            min={0}
                                            max={100}
                                            step={1}
                                            value={quadrantConfig.subRect.xmin}
                                            onChange={(e) => {
                                                const value = e?.target?.value;
                                                setQuadrantConfig(prev => sanitizeQuadrantConfig({
                                                    ...prev,
                                                    subRect: { ...prev.subRect, xmin: value },
                                                }));
                                            }}
                                        />
                                    </MiniField>
                                    <MiniField>
                                        <MiniLabel>Xmax</MiniLabel>
                                        <MiniInput
                                            type="number"
                                            min={0}
                                            max={100}
                                            step={1}
                                            value={quadrantConfig.subRect.xmax}
                                            onChange={(e) => {
                                                const value = e?.target?.value;
                                                setQuadrantConfig(prev => sanitizeQuadrantConfig({
                                                    ...prev,
                                                    subRect: { ...prev.subRect, xmax: value },
                                                }));
                                            }}
                                        />
                                    </MiniField>
                                    <MiniField>
                                        <MiniLabel>Ymin</MiniLabel>
                                        <MiniInput
                                            type="number"
                                            min={0}
                                            max={100}
                                            step={1}
                                            value={quadrantConfig.subRect.ymin}
                                            onChange={(e) => {
                                                const value = e?.target?.value;
                                                setQuadrantConfig(prev => sanitizeQuadrantConfig({
                                                    ...prev,
                                                    subRect: { ...prev.subRect, ymin: value },
                                                }));
                                            }}
                                        />
                                    </MiniField>
                                    <MiniField>
                                        <MiniLabel>Ymax</MiniLabel>
                                        <MiniInput
                                            type="number"
                                            min={0}
                                            max={100}
                                            step={1}
                                            value={quadrantConfig.subRect.ymax}
                                            onChange={(e) => {
                                                const value = e?.target?.value;
                                                setQuadrantConfig(prev => sanitizeQuadrantConfig({
                                                    ...prev,
                                                    subRect: { ...prev.subRect, ymax: value },
                                                }));
                                            }}
                                        />
                                    </MiniField>
                                </SubRow>
                            </ModalSection>

                            <ModalSection>
                                <ModalSectionTitle>Risk thresholds (Bottom 2%)</ModalSectionTitle>
                                <SubRow>
                                    <MiniField>
                                        <MiniLabel>Very High ≥</MiniLabel>
                                        <MiniInput
                                            type="number"
                                            min={0}
                                            max={100}
                                            step={1}
                                            value={riskThresholds.veryHigh}
                                            onChange={(e) => {
                                                const value = e?.target?.value;
                                                setRiskThresholds(prev => ({ ...prev, veryHigh: Number(value) }));
                                            }}
                                        />
                                    </MiniField>
                                    <MiniField>
                                        <MiniLabel>High ≥</MiniLabel>
                                        <MiniInput
                                            type="number"
                                            min={0}
                                            max={100}
                                            step={1}
                                            value={riskThresholds.high}
                                            onChange={(e) => {
                                                const value = e?.target?.value;
                                                setRiskThresholds(prev => ({ ...prev, high: Number(value) }));
                                            }}
                                        />
                                    </MiniField>
                                    <MiniField>
                                        <MiniLabel>Moderate ≥</MiniLabel>
                                        <MiniInput
                                            type="number"
                                            min={0}
                                            max={100}
                                            step={1}
                                            value={riskThresholds.moderate}
                                            onChange={(e) => {
                                                const value = e?.target?.value;
                                                setRiskThresholds(prev => ({ ...prev, moderate: Number(value) }));
                                            }}
                                        />
                                    </MiniField>
                                    <MiniField>
                                        <MiniLabel>Stable ≥</MiniLabel>
                                        <MiniInput
                                            type="number"
                                            min={0}
                                            max={100}
                                            step={1}
                                            value={riskThresholds.stable}
                                            onChange={(e) => {
                                                const value = e?.target?.value;
                                                setRiskThresholds(prev => ({ ...prev, stable: Number(value) }));
                                            }}
                                        />
                                    </MiniField>
                                    <MiniField>
                                        <MiniLabel>Low ≥</MiniLabel>
                                        <MiniInput
                                            type="number"
                                            min={0}
                                            max={100}
                                            step={1}
                                            value={riskThresholds.low}
                                            onChange={(e) => {
                                                const value = e?.target?.value;
                                                setRiskThresholds(prev => ({ ...prev, low: Number(value) }));
                                            }}
                                        />
                                    </MiniField>
                                </SubRow>
                            </ModalSection>
                        </ModalBody>
                    </ModalCard>
                </ModalOverlay>,
                document.body
            )}
        </Container>
    );
};

export default CustomAnalytics;

const QuadrantIcon = () => (
    <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <rect
            x="3"
            y="3"
            width="18"
            height="18"
            rx="3.5"
            stroke="#1F2937"
            strokeWidth="1.6"
        />
        <path
            d="M12 3v18M3 12h18"
            stroke="#94a3b8"
            strokeWidth="1.6"
            strokeLinecap="round"
        />
        <circle cx="16.5" cy="7.5" r="1.6" fill="#1F2937" opacity="0.85" />
        <circle cx="7.5" cy="16.5" r="1.6" fill="#1F2937" opacity="0.85" />
    </svg>
);

const Container = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    font-family: 'Raleway';
`;

const HeaderRow = styled.div`
    display: flex;
    flex-direction: column;
`;

const BlueTitle = styled.div`
    color: #2D70E2;
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 15px;
    margin-top: -20px;
`;

const Title = styled.div`
    font-size: 28px;
    font-weight: bold;
    margin-bottom: 30px;
    color: #2A3039;
    display: flex;
    align-items: center;
    gap: 10px;
`;

const TitleIconWrapper = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 8px;
    background: #F8F9FA;
    border-radius: 7px;
    color: #1F2937;
`;

const OptionsContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    min-height: 40px;
    gap: 24px;
`;

const Controls = styled.div`
    display: flex;
    gap: 16px;
`;

const TopRightActions = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const ControlGroup = styled.label`
    display: flex;
    flex-direction: column;
    gap: 6px;
    font-size: 12px;
    color: #6B7280;
`;

const Label = styled.span``;

const Select = styled.select`
    background: white;
    border: 1px solid #e1e5e9;
    border-radius: 6px;
    padding: 6px 10px;
    font-size: 12px;
    color: #2A3039;
`;

const Input = styled.input`
    background: white;
    border: 1px solid #e1e5e9;
    border-radius: 6px;
    padding: 6px 10px;
    font-size: 12px;
    color: #2A3039;
    width: 140px;
`;

const SubRow = styled.div`
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
`;

const MiniField = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const MiniLabel = styled.span`
    font-size: 10px;
    color: #9CA3AF;
`;

const MiniInput = styled.input`
    background: white;
    border: 1px solid #e1e5e9;
    border-radius: 6px;
    padding: 6px 8px;
    font-size: 12px;
    color: #2A3039;
    width: 70px;
`;

const FiltersRow = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;
    margin-top: 10px;
    margin-bottom: 10px;
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const Section = styled.div`
    width: 100%;
    padding: 0 4px;
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
`;

const Actions = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const SummaryRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 0 0 10px 0;
`;

const SummaryRight = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const ModeToggle = styled.div`
    display: inline-flex;
    align-items: center;
    border: 1px solid #e1e5e9;
    border-radius: 10px;
    overflow: hidden;
    background: #ffffff;
`;

const ModeButton = styled.button`
    appearance: none;
    border: 0;
    background: ${p => (p.$active ? '#f3f4f6' : '#ffffff')};
    color: #2A3039;
    padding: 8px 10px;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover {
        background: ${p => (p.$active ? '#eef2f6' : '#f8fafc')};
    }
`;

const SummaryLeft = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const SummaryTitle = styled.div`
    font-size: 12px;
    font-weight: 700;
    color: #2A3039;
`;

const SummaryMeta = styled.div`
    font-size: 12px;
    color: #6B7280;
`;

const SeverityBarWrap = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin: 0 0 10px 0;
`;

const SeverityBar = styled.div`
    width: 100%;
    height: 14px;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid #e1e5e9;
    display: flex;
    background: #ffffff;
`;

const SeveritySegment = styled.div`
    height: 100%;
`;

const SeverityCounts = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px 16px;
    color: #2A3039;
    font-size: 12px;
`;

const SeverityCount = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 8px;
`;

const LegendSwatch = styled.div`
    width: 10px;
    height: 10px;
    border-radius: 2px;
    flex: 0 0 auto;
`;

const CsvButton = styled.button`
    background: #ffffff;
    border: 1px solid #e1e5e9;
    color: #2A3039;
    border-radius: 8px;
    padding: 8px 10px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: border-color 0.15s ease, transform 0.15s ease;

    &:hover {
        border-color: #c7cdd6;
        transform: translateY(-1px);
    }

    &:disabled {
        opacity: 0.55;
        cursor: not-allowed;
        transform: none;
    }
`;

const GhostButton = styled(CsvButton)`
    padding: 8px 12px;
`;

const SectionTitle = styled.div`
    font-size: 16px;
    font-weight: 600;
    color: #2A3039;
`;

const TableWrap = styled.div`
    width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    border: 1px solid #e1e5e9;
    border-radius: 10px;
    position: relative;
    margin-top: 22px;
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
`;

const TableOverlay = styled.div`
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, 0.72);
    backdrop-filter: blur(1px);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    z-index: 2;
`;

const Spinner = styled.div`
    width: 14px;
    height: 14px;
    border-radius: 999px;
    border: 2px solid #e1e5e9;
    border-top-color: #2D70E2;
    animation: spin 0.85s linear infinite;

    @keyframes spin {
        to { transform: rotate(360deg); }
    }
`;

const OverlayText = styled.div`
    font-size: 12px;
    color: #2A3039;
    font-weight: 600;
`;

const ThBase = styled.th`
    position: relative;
    text-align: left;
    font-size: 10px;
    font-weight: 700;
    color: #2A3039;
    padding: 8px 10px;
    border-bottom: 1px solid #e1e5e9;
    background: #ffffff;
    user-select: none;
    cursor: pointer;
    vertical-align: bottom;
    line-height: 1.15;
    overflow: hidden;
`;

const Th = styled(ThBase)``;

const ThRight = styled(ThBase)`
    text-align: right;
`;

const ThRightHighlight = styled(ThRight)`
    background: #f3f4f6;
`;

const ThInner = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 8px;
`;

const HeaderText = styled.span`
    white-space: nowrap;
`;

const SortHint = styled.span`
    color: ${p => (p.active ? '#2D70E2' : '#9CA3AF')};
    font-weight: 700;
    font-size: 12px;
    min-width: 14px;
    text-align: center;
`;

const Tr = styled.tr`
    &:hover td {
        background: #fbfcff;
    }

    &:hover td[data-highlight="true"] {
        background: #f3f4f6;
    }
`;

const Td = styled.td`
    padding: 12px 14px;
    border-bottom: 1px solid #f0f2f5;
    font-size: 12px;
    color: #2A3039;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const TdRight = styled(Td)`
    text-align: right;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
`;

const TdRightHighlight = styled(TdRight).attrs({ 'data-highlight': 'true' })`
    background: #f3f4f6;
`;

const NameCell = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
`;

const NameLine = styled.div`
    display: flex;
    align-items: baseline;
    gap: 6px;
    min-width: 0;
    white-space: nowrap;
`;

const NamePrimary = styled.span`
    font-weight: 800;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const NameCount = styled.span`
    font-weight: 500;
    color: #6B7280;
    flex: 0 0 auto;
`;

const Meta = styled.div`
    color: #9CA3AF;
    font-size: 11px;
`;

const EmptyInline = styled.div`
    padding: 10px 0;
    color: #6B7280;
`;

const RiskLabel = styled.span`
    font-weight: 700;
    font-size: 11px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: inline-block;
    max-width: 100%;
`;

const EmptyState = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 200px;
    padding: 40px;
    color: #6B7280;
`;

const ModalOverlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.28);
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 18px 22px 22px 22px;
    z-index: 9999;
`;

const ModalCard = styled.div`
    width: 100%;
    max-width: 760px;
    border-radius: 12px;
    background: #ffffff;
    border: 1px solid #e1e5e9;
    box-shadow: 0 16px 40px rgba(15, 23, 42, 0.18);
    max-height: calc(100vh - 44px);
    overflow: hidden;
`;

const ModalHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    border-bottom: 1px solid #eef2f6;
`;

const ModalTitle = styled.div`
    font-size: 14px;
    font-weight: 700;
    color: #2A3039;
`;

const ModalClose = styled.button`
    background: #ffffff;
    border: 1px solid #e1e5e9;
    color: #2A3039;
    border-radius: 8px;
    padding: 8px 10px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
`;

const ModalBody = styled.div`
    padding: 16px;
    overflow: auto;
`;

const ModalSection = styled.div`
    margin-bottom: 18px;
`;

const ModalSectionTitle = styled.div`
    font-size: 12px;
    font-weight: 700;
    color: #2A3039;
    margin-bottom: 10px;
`;

const SettingsGrid = styled.div`
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
`;
